<template>
  <v-row class="home-charts">
    <v-col v-for="chart in charts" :key="chart.title">
      <chart-card :data="chart.data"
                  :options="chart.options"
                  :responsive-options="chart.responsiveOptions"
                  :title="chart.title"
                  :type="chart.type"
      />
    </v-col>
  </v-row>
</template>
<script>
import ChartCard from '@/components/Core/ChartCard'
import { mapActions } from 'vuex'

export default {
  name: 'ChartsSlider',
  components: {
    ChartCard
  },
  data: () => ({
    auction_daily_qty_data: [],
    auction_daily_qty_loading: true,
    auction_daily_value_data: [],
    auction_daily_value_loading: true,
    inventory_daily_qty_data: [],
    inventory_daily_qty_loading: true,
    inventory_daily_value_data: [],
    inventory_daily_value_loading: true,
  }),
  computed: {
    // ---- HELPERS -------------------------------------------------

    sortedAuctionQty() {
      return [...this.auction_daily_qty_data].sort((a, b) =>
          new Date(a.created_at) - new Date(b.created_at)
      )
    },

    sortedAuctionValue() {
      return [...this.auction_daily_value_data].sort((a, b) =>
          new Date(a.created_at) - new Date(b.created_at)
      )
    },

    sortedInventoryQty() {
      return [...this.inventory_daily_qty_data].sort((a, b) =>
          new Date(a.created_at) - new Date(b.created_at)
      )
    },

    sortedInventoryValue() {
      return [...this.inventory_daily_value_data].sort((a, b) =>
          new Date(a.created_at) - new Date(b.created_at)
      )
    },

    // ---- AUCTION -------------------------------------------------

    auctionDailyLabels() {
      return this.sortedAuctionQty.map(d => d.label)
    },

    auctionDailySeries() {
      return [
        this.sortedAuctionQty.map(d => d.value),
        this.sortedAuctionValue.map(d => d.value)
      ]
    },

    auctionDailyHigh() {
      const flat = this.auctionDailySeries.flat()
      const high = (Math.round(Math.max(...flat) / 1000) * 1000)
      return Math.round(high + high * 0.05)
    },

    auctionDailyLow() {
      const flat = this.auctionDailySeries.flat()
      return Math.round(Math.min(...flat) / 1000) * 1000
    },

    // ---- INVENTORY -------------------------------------------------

    inventoryDailyLabels() {
      return this.sortedInventoryQty.map(d => d.label)
    },

    inventoryDailySeries() {
      return [
        this.sortedInventoryQty.map(d => d.value),
        this.sortedInventoryValue.map(d => d.value)
      ]
    },

    inventoryDailyHigh() {
      const flat = this.inventoryDailySeries.flat()
      const high = (Math.round(Math.max(...flat) / 1000) * 1000)
      return Math.round(high + high * 0.05)
    },

    inventoryDailyLow() {
      const flat = this.inventoryDailySeries.flat()
      return Math.round(Math.min(...flat) / 1000) * 1000
    },

    // ---- CHARTS -------------------------------------------------

    charts() {
      return [
        {
          type: 'Line',
          title: 'Auction Daily',
          subtitle: 'Daily Auction Performance',
          time: 'updated yesterday',
          data: {
            labels: this.auctionDailyLabels,
            series: this.auctionDailySeries,
          },
          options: {
            axisX: { showGrid: false },
            axisY: {
              showGrid: false,
              labelInterpolationFnc: value => (value / 1000) + 'k',
            },
            low: this.auctionDailyLow,
            high: this.auctionDailyHigh,
            chartPadding: { top: 0, right: 5, bottom: 0, left: 0 },
          },
          responsiveOptions: [
            [
              'screen and (max-width: 640px)',
              {
                seriesBarDistance: 5,
                axisX: {
                  labelInterpolationFnc: value => value[0],
                },
              }
            ]
          ],
        },

        {
          type: 'Line',
          color: 'success',
          title: 'Daily Sales',
          subtitle:
              '<i class="mdi mdi-arrow-up green--text"></i>' +
              '<span class="green--text">55%</span>&nbsp;increase in today\'s sales',
          time: 'updated 4 minutes ago',
          data: {
            labels: this.inventoryDailyLabels,
            series: this.inventoryDailySeries,
          },
          options: {
            axisX: { showGrid: false },
            axisY: {
              showGrid: false,
              labelInterpolationFnc: value => (value / 1000) + 'k',
            },
            low: this.inventoryDailyLow,
            high: this.inventoryDailyHigh,
            chartPadding: { top: 0, right: 0, bottom: 0, left: 0 },
          },
        },

        {
          type: 'Line',
          color: 'info',
          title: 'Completed Tasks',
          subtitle: 'Last Campaign Performance',
          time: 'campaign sent 26 minutes ago',
          data: {
            labels: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
            series: [[12, 17, 7, 17, 23, 18, 38]],
          },
          options: {
            axisX: { showGrid: false },
            axisY: { showGrid: false },
            low: 0,
            high: 50,
            chartPadding: { top: 0, right: 0, bottom: 0, left: 0 },
          },
        },
      ]
    }
  },
  mounted () {
    this.loadChartData({
      type: 'auction_daily_qty'
    })
      .then(result => {
        this.auction_daily_qty_data = result.data
      })
      .finally(() => {
        this.auction_daily_qty_loading = false
      })
    this.loadChartData({
      type: 'auction_daily_value'
    })
      .then(result => {
        this.auction_daily_value_data = result.data
      })
      .finally(() => {
        this.auction_daily_value_loading = false
      })

    this.loadChartData({
      type: 'inventory_daily_qty'
    })
      .then(result => {
        this.inventory_daily_qty_data = result.data
      })
      .finally(() => {
        this.inventory_daily_qty_loading = false
      })
    this.loadChartData({
      type: 'inventory_daily_value'
    })
      .then(result => {
        this.inventory_daily_value_data = result.data
      })
      .finally(() => {
        this.inventory_daily_value_loading = false
      })
  },
  methods: {
    ...mapActions({
      loadChartData: 'statistics/loadChartData'
    })
  }
}
</script>
<style lang="scss" scoped>
.home-charts {
  height: 200px;
  margin: 50px 0;
}
</style>
